import type { LoginResponse } from '@/modules/auth/types';

export type SocialProvider = 'google' | 'apple';

type SocialAuthPayload = {
  data?: LoginResponse;
  message?: string;
  error_code?: string;
};

const SOCIAL_REDIRECT_STORAGE_KEY = 'tradel.social.redirect';
const API_PREFIX = '/api/v1';

const SOCIAL_AUTH_ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Apple sign in was cancelled.',
  apple_login_failed: 'Apple sign in failed. Please try again.',
  SOCIAL_EMAIL_REQUIRED:
    'Apple did not provide an email. Try again with email sharing enabled or sign in with email and password.',
  SOCIAL_ACCOUNT_CONFLICT: 'This social account cannot be linked safely. Sign in with your existing method.',
  ACCOUNT_BLOCKED: 'Your account is blocked. Contact support.',
  ACCOUNT_DEACTIVATED: 'Your account is deactivated. Contact support.',
  INVALID_CREDENTIALS: 'Social sign in failed. Please try again.',
};

function getApiBaseUrl(): string {
  const apiUrl = useRuntimeConfig().public.apiUrl;

  return String(apiUrl || '').replace(/\/+$/, '');
}

function getSocialApiBaseUrl(): string {
  const apiBaseUrl = getApiBaseUrl();

  if (!apiBaseUrl || apiBaseUrl.endsWith(API_PREFIX)) {
    return apiBaseUrl;
  }

  return `${apiBaseUrl}${API_PREFIX}`;
}

function getSafeRedirectTarget(redirect?: string | null): string | null {
  if (!redirect || !redirect.startsWith('/') || redirect.startsWith('//')) {
    return null;
  }

  return redirect;
}

function getPayload(error: unknown): SocialAuthPayload | undefined {
  if (error && typeof error === 'object' && 'data' in error) {
    return (error as { data?: SocialAuthPayload }).data;
  }

  if (error && typeof error === 'object') {
    return error as SocialAuthPayload;
  }

  return undefined;
}

export function getSocialAuthErrorCode(error: unknown): string {
  const payload = getPayload(error);

  return payload?.error_code || 'INVALID_CREDENTIALS';
}

export function getSocialAuthErrorMessage(error: unknown): string {
  const code = getSocialAuthErrorCode(error);

  return (
    SOCIAL_AUTH_ERROR_MESSAGES[code] || getPayload(error)?.message || SOCIAL_AUTH_ERROR_MESSAGES.INVALID_CREDENTIALS
  );
}

export function useSocialAuth() {
  const route = useRoute();
  const router = useRouter();

  function buildSocialUrl(provider: SocialProvider, action: 'redirect' | 'callback' | 'exchange', search = ''): string {
    const apiBaseUrl = getSocialApiBaseUrl();

    if (!apiBaseUrl) {
      throw new Error('API URL is not configured.');
    }

    return `${apiBaseUrl}/auth/social/${provider}/${action}${search}`;
  }

  function rememberRedirectTarget(): void {
    if (import.meta.server) {
      return;
    }

    const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : null;
    const safeRedirect = getSafeRedirectTarget(redirect);

    try {
      if (safeRedirect) {
        window.sessionStorage.setItem(SOCIAL_REDIRECT_STORAGE_KEY, safeRedirect);
      } else {
        window.sessionStorage.removeItem(SOCIAL_REDIRECT_STORAGE_KEY);
      }
    } catch {
      // Social login can proceed without preserving the optional post-login redirect.
    }
  }

  function consumeRedirectTarget(): string | null {
    if (import.meta.server) {
      return null;
    }

    try {
      const redirect = getSafeRedirectTarget(window.sessionStorage.getItem(SOCIAL_REDIRECT_STORAGE_KEY));
      window.sessionStorage.removeItem(SOCIAL_REDIRECT_STORAGE_KEY);

      return redirect;
    } catch {
      return null;
    }
  }

  function startSocialLogin(provider: SocialProvider): void {
    if (import.meta.server) {
      return;
    }

    rememberRedirectTarget();
    window.location.assign(buildSocialUrl(provider, 'redirect'));
  }

  async function completeGoogleSocialLogin(search: string): Promise<LoginResponse> {
    const response = await fetch(buildSocialUrl('google', 'callback', search), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    const payload = (await response.json().catch(() => ({}))) as SocialAuthPayload;

    if (!response.ok || !payload.data) {
      throw { data: payload };
    }

    return payload.data;
  }

  async function completeAppleSocialLogin(code: string): Promise<LoginResponse> {
    const response = await fetch(buildSocialUrl('apple', 'exchange'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    const payload = (await response.json().catch(() => ({}))) as SocialAuthPayload;

    if (!response.ok || !payload.data) {
      throw { data: payload };
    }

    return payload.data;
  }

  function hasProviderError(query: Record<string, unknown>): boolean {
    return typeof query.error === 'string' || typeof query.error_description === 'string';
  }

  async function redirectToSocialAuthError(error: unknown): Promise<void> {
    await router.replace({
      path: '/login',
      query: {
        social_error: getSocialAuthErrorCode(error),
      },
    });
  }

  return {
    completeAppleSocialLogin,
    completeGoogleSocialLogin,
    consumeRedirectTarget,
    getSocialAuthErrorMessage,
    hasProviderError,
    redirectToSocialAuthError,
    startSocialLogin,
  };
}
